
import { useLocalSubscriptionCheck } from "./subscription/useLocalSubscriptionCheck";
import { useStripeSubscriptionCheck } from "./subscription/useStripeSubscriptionCheck";
import { SubscriptionStatus } from "@/services/subscription";

interface UseSubscriptionCheckerProps {
  lastCheckedStatus: SubscriptionStatus | null;
  isSubscriptionActive: (status: SubscriptionStatus | null) => boolean;
  hasFreeTransforms: (status: SubscriptionStatus | null) => boolean;
  isLocallyConfirmedActive: () => boolean;
  hasCheckedSubscriptionRef: React.MutableRefObject<boolean>;
  isCheckingSubscriptionRef: React.MutableRefObject<boolean>;
  setLastCheckedStatus: (status: SubscriptionStatus | null) => void;
  forceRefresh?: () => void;
  onLoadSubscriptionStatus: (userId: string) => Promise<SubscriptionStatus | null>;
}

export const useSubscriptionChecker = ({
  lastCheckedStatus,
  isSubscriptionActive,
  hasFreeTransforms,
  isLocallyConfirmedActive,
  hasCheckedSubscriptionRef,
  isCheckingSubscriptionRef,
  setLastCheckedStatus,
  forceRefresh,
  onLoadSubscriptionStatus
}: UseSubscriptionCheckerProps) => {
  const { checkLocalSubscription } = useLocalSubscriptionCheck({
    lastCheckedStatus,
    isSubscriptionActive,
    isLocallyConfirmedActive,
    hasCheckedSubscriptionRef
  });

  const { checkStripeSubscription } = useStripeSubscriptionCheck({
    lastCheckedStatus,
    isSubscriptionActive,
    isCheckingSubscriptionRef,
    setLastCheckedStatus,
    hasCheckedSubscriptionRef,
    forceRefresh,
    onLoadSubscriptionStatus
  });

  const checkSubscriptionStatus = async (userId: string): Promise<boolean> => {
    // Check local subscription first
    if (checkLocalSubscription()) {
      return true;
    }

    // Check Stripe subscription next
    if (await checkStripeSubscription(userId)) {
      return true;
    }

    // Check current status
    const currentStatus = lastCheckedStatus;
    if (currentStatus && isSubscriptionActive(currentStatus)) {
      localStorage.setItem('subscription_confirmed_active', 'true');
      window.dispatchEvent(new Event('subscription_updated'));
      hasCheckedSubscriptionRef.current = true;
      return true;
    }

    // Final check for free transforms
    if (!isCheckingSubscriptionRef.current) {
      isCheckingSubscriptionRef.current = true;
      const freshStatus = await onLoadSubscriptionStatus(userId);
      isCheckingSubscriptionRef.current = false;
      
      if (freshStatus) {
        setLastCheckedStatus(freshStatus);
        if (hasFreeTransforms(freshStatus)) {
          return true;
        }
      }
    }

    return false;
  };

  return { checkSubscriptionStatus };
};
